import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Leaf, Users, Lightbulb, Zap } from 'lucide-react';

const policies = [
  {
    icon: <ShieldCheck size={36} />,
    title: "Yüksek Güvenlik ve Dayanıklılık",
    desc: "Tüm yapılarımız, en güncel deprem yönetmeliklerine ve uluslararası güvenlik standartlarına uygun olarak tasarlanıp inşa edilmektedir.",
    color: "text-blue-600",
    lightBg: "bg-blue-50"
  },
  {
    icon: <Leaf size={36} />,
    title: "Çevre Dostu Yaklaşım",
    desc: "Doğal kaynakları verimli kullanan, atık yönetimini optimize eden ve yeşil bina sertifikasyonlarına uyumlu üretim modelleri benimsiyoruz.",
    color: "text-emerald-500",
    lightBg: "bg-emerald-50"
  },
  {
    icon: <Users size={36} />,
    title: "Üye Odaklı Hizmet",
    desc: "Kooperatif üyelerimizin beklentilerini proaktif bir şekilde analiz ediyor, zamanında ve şeffaf bilgilendirme ile tam memnuniyet hedefliyoruz.",
    color: "text-amber-500",
    lightBg: "bg-amber-50"
  },
  {
    icon: <Lightbulb size={36} />,
    title: "İnovatif Teknoloji",
    desc: "Altyapı ve üstyapı süreçlerinde akıllı bina sistemlerini ve en son mühendislik teknolojilerini entegre ediyoruz.",
    color: "text-violet-600",
    lightBg: "bg-violet-50"
  },
  {
    icon: <CheckCircle2 size={36} />,
    title: "Sürekli İyileştirme",
    desc: "Kalite yönetim sistemimizi periyodik olarak denetliyor, süreçlerimizi ölçülebilir veriler ışığında sürekli geliştiriyoruz.",
    color: "text-rose-600",
    lightBg: "bg-rose-50"
  },
  {
    icon: <Zap size={36} />,
    title: "İş Sağlığı ve Güvenliği",
    desc: "Şantiye sahalarımızda ve ofislerimizde 'Sıfır İş Kazası' vizyonu ile çalışıyor, personelimizi düzenli eğitimlerle destekliyoruz.",
    color: "text-cyan-600",
    lightBg: "bg-cyan-50"
  }
];

const QualityPolicy = () => {
  return (
    <div className="pt-32 pb-24 min-h-screen bg-slate-50 relative overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <span className="inline-block py-1.5 px-4 rounded-full bg-blue-100 text-blue-700 font-bold text-sm mb-4">Sıfır Taviz</span>
          <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 mb-6 tracking-tight">Kalite Politikamız</h1>
          <p className="text-xl text-slate-600 font-medium leading-relaxed">
            S.S. İkitelli - Silivri Toplu İşyeri Yapı Kooperatifi olarak, kaliteyi bir süreç değil, kültür olarak benimsiyoruz. Her projemizde mükemmelliği hedefliyoruz.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {policies.map((policy, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="bg-white p-10 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-transform duration-300 group"
            >
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-8 shadow-lg ${policy.lightBg}`}>
                <div className={`${policy.color}`}>
                  {policy.icon}
                </div>
              </div>
              <h3 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">{policy.title}</h3>
              <p className="text-slate-600 font-medium leading-relaxed text-lg">
                {policy.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 bg-slate-900 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl"
        >
          {/* Dynamic colorful blobs inside the dark section */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full mix-blend-screen filter blur-[100px] opacity-40 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-600 rounded-full mix-blend-screen filter blur-[100px] opacity-40 animate-pulse" style={{ animationDelay: '2s' }}></div>
          
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl mb-8 border border-white/20">
              <CheckCircle2 size={40} className="text-white" />
            </div>
            <h3 className="text-4xl md:text-5xl font-extrabold mb-6 text-white tracking-tight">Uluslararası Standartlar</h3>
            <p className="text-blue-100/80 text-xl font-medium leading-relaxed">
              Tüm operasyonlarımız ve yönetim süreçlerimiz ISO 9001:2015 kalite standartlarına uygun olarak belgelendirme süreçlerinden geçirilmek üzere titizlikle yapılandırılmıştır.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QualityPolicy;
